const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };
const SENSITIVE_KEY = /authorization|cookie|token|password|secret|credential|api[-_]?key|phone|email/i;
const MAX_DEPTH = 5;

const redactPhoneNumbers = value => value.replace(
    /(^|[^\dA-Za-z])(\+?\d[\d ()-]{7,20}\d)(?![\dA-Za-z])/g,
    (match, prefix, candidate) => {
        if (/^\d{4}-\d{2}-\d{2}(?:[ T]\d{2})?$/.test(candidate)) return match;
        const digitCount = candidate.replace(/\D/g, '').length;
        return digitCount >= 10 && digitCount <= 15
            ? `${prefix}[REDACTED_PHONE]`
            : match;
    }
);

const sanitizeString = value => redactPhoneNumbers(value
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]'));

const configuredLevel = () => {
    const requested = process.env.REACT_APP_LOG_LEVEL?.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(LEVELS, requested)) return requested;
    return process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
};

const serialize = (value, depth = 0, seen = new WeakSet()) => {
    if (value === null || value === undefined) return value;
    if (value instanceof Error) {
        return {
            name: value.name,
            message: sanitizeString(value.message || ''),
            stack: value.stack ? sanitizeString(value.stack) : undefined,
            ...(value.statusCode && { statusCode: value.statusCode }),
        };
    }
    if (typeof value === 'string') return sanitizeString(value);
    if (typeof value !== 'object') return value;
    if (depth >= MAX_DEPTH) return '[Max depth]';
    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    if (Array.isArray(value)) {
        return value.slice(0, 50).map(item => serialize(item, depth + 1, seen));
    }

    return Object.entries(value).reduce((result, [key, item]) => {
        result[key] = SENSITIVE_KEY.test(key)
            ? '[Redacted]'
            : serialize(item, depth + 1, seen);
        return result;
    }, {});
};

export const createRequestId = () => {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const log = (level, event, context = {}) => {
    const normalizedLevel = Object.prototype.hasOwnProperty.call(LEVELS, level) ? level : 'info';
    if (LEVELS[normalizedLevel] < LEVELS[configuredLevel()]) return;

    const entry = serialize({
        timestamp: new Date().toISOString(),
        level: normalizedLevel,
        event,
        ...context,
    });
    const output = console[normalizedLevel] || console.log;
    output(`[boozeon-web] ${event}`, entry);
};

export const logger = {
    debug: (event, context) => log('debug', event, context),
    info: (event, context) => log('info', event, context),
    warn: (event, context) => log('warn', event, context),
    error: (event, context) => log('error', event, context),
};

export default logger;
